import { SolomonLogo, useOrgAuth, useOrgAuthStore } from "@solomon-apps/shared";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import type { OnboardingPurpose } from "@solomon-apps/shared/src/types/clientOnboardingPolicy";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import {
    ArrowRightLeftLongIcon,
    TrashIcon
} from "@solomon-labs/design/components/icons";
import { SectionHeading } from "@solomon-labs/design/components/layout";
import {
    Alert,
    AlertDescription,
    AlertTitle,
    Badge,
    ButtonIcon,
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
    Checkbox,
    Button as DSButton,
    FieldError as DSFieldError,
    Field,
    FieldLabel,
    FieldTitle,
    Input,
    List,
    ListItem,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    ToggleGroup,
    ToggleGroupItem,
    UploadFiles
} from "@solomon-labs/design/components/ui";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "motion/react";
import {
    type Dispatch,
    type ReactElement,
    type ReactNode,
    type SetStateAction,
    useEffect,
    useRef,
    useState
} from "react";

import { CountrySelect } from "@/components/CountrySelect";
import { LegalEntityTypeSelect } from "@/components/LegalEntityTypeSelect";
import { CLIENT_OAUTH_CONFIG } from "@/constants/oauthConfig";
import {
    EMPTY_LEGAL_ENTITY_TYPE_DRAFT,
    type LegalEntityTypeDraft
} from "@/data/legalEntityTypes";
import { useClientPlatformAccess } from "@/hooks/useClientPlatformAccess";
import { AgreementsOutCard } from "@/pages/Onboarding/AgreementsOutCard";
import type {
    CapitalRange,
    UseCaseId
} from "@/pages/Onboarding/forms/useCaseFormShared";
import { CelebrationCard } from "@/pages/Onboarding/OnboardingShell";
import { FormCard } from "@/pages/Onboarding/panel/FormCard";
import {
    DOCUMENT_ACCEPT,
    DOCUMENT_MAX_SIZE
} from "@/pages/Onboarding/panel/uploadConstraints";
import { PendingReviewCard } from "@/pages/Onboarding/PendingReviewCard";
import { useApprovalAcknowledgement } from "@/pages/Onboarding/useApprovalAcknowledgement";
import { useOnboardingApplication } from "@/pages/Onboarding/useOnboardingApplication";
import {
    type IncorporationJurisdiction,
    submitLite
} from "@/services/clientOnboarding";
import {
    listOnboardingDocuments,
    uploadOnboardingDocument
} from "@/services/clientOnboardingDocuments";
import { isSimMode } from "@/services/sim/simMode";
import { resolveClientFormError } from "@/utils/clientFormErrors";

import { truncateAddress } from "../shared/format";

import {
    buildLitePayload,
    EMPTY_LITE_WALLET,
    isMintingOnlyApplication,
    LITE_REQUIRED_FIELD_ORDER,
    liteFieldErrors,
    type LiteFormationDocument,
    type LiteFormState,
    type LiteRequiredField,
    liteScreen,
    liteSignsMintingAgreement,
    type LiteWalletDraft,
    nextLiteWalletId,
    prefillLiteForm
} from "./onboardingLiteForm";

/** Order Form §2.3's per-address asset column. The assets the Program
 *  supports today. */
const PRIMARY_ASSET_OPTIONS = ["USDC", "USDG", "PYUSD"] as const;

/** What the applicant agrees to before submitting. One link for the whole
 *  console, so the address is not repeated per surface. */
const TERMS_OF_SERVICE_URL = "https://solomonlabs.org/terms-of-use";

/** One badge colour per asset, so a row's assets stay tellable apart at a
 *  glance without reading the labels. */
const ASSET_BADGE_COLOR = {
    USDC: "primary",
    USDG: "septenary",
    PYUSD: "senary"
} as const;

/**
 * Lite (single-step MVP) onboarding view. Rendered at `/onboarding` in place
 * of the full `OnboardingHub` when the server-owned lite-onboarding flag is
 * on (`liteOnboardingEnabled` on the application bundle; see
 * `OnboardingEntry` in `pages/Onboarding`). A confirm-and-correct form:
 * the org's name + legal name are pre-seeded by the issuer, so we prefill both
 * and let the client edit. Submitting persists the identity + intent fields
 * plus what the agreements print (the Parties clause, the signatory, and,
 * for a client the issuer has enabled Mint and Redeem for, the declared
 * mint/redeem addresses the Minting Master Agreement carries; see
 * `liteSignsMintingAgreement`), the entity's formation document and the
 * applicant's acceptance of the Terms of Service, and submits the
 * application for manual issuer review; the client then sees the
 * pending-review screen, then the agreements-out screen while the
 * envelopes are signed, and the `OnboardingGate` (which keys on
 * `status === "approved"`) keeps them on `/onboarding` until then. The
 * formation document is the one upload. Authorized addresses use the same
 * signed wallet challenge as full onboarding.
 *
 * Visuals follow the full hub's design: the same "{Org} Onboarding" page
 * header (see `LiteHeader`), `FormCard` per field, and submit-rail treatment
 * as the structured forms in `pages/Onboarding`. The form itself stays one
 * single-column page: with only seven fields, a step rail would be ceremony,
 * so the task title sits below the header as an intro block instead. The
 * full `OnboardingHub` is left completely untouched.
 *
 * Temporary: removed alongside the flag when the full flow ships.
 */

const USE_CASE_OPTIONS: Array<{ value: UseCaseId; label: string }> = [
    { value: "treasury", label: "Treasury" },
    { value: "lp", label: "Liquidity Provider" },
    { value: "pol", label: "Protocol-Owned Liquidity" },
    { value: "neobank-d2u", label: "Neobank" },
    { value: "other", label: "Other" }
];

const USE_CASE_VALUES: ReadonlySet<string> = new Set(
    USE_CASE_OPTIONS.map((opt) => opt.value)
);

function isUseCaseId(value: unknown): value is UseCaseId {
    return typeof value === "string" && USE_CASE_VALUES.has(value);
}

const CAPITAL_RANGE_OPTIONS: Array<{ value: CapitalRange; label: string }> = [
    { value: "<500k", label: "<$500k" },
    { value: "500k-2m", label: "$500k–$2M" },
    { value: "2m-10m", label: "$2M–$10M" },
    { value: "10m+", label: "$10M+" }
];

export function OnboardingLite(): ReactElement {
    const me = useOrgAuthStore((s) => s.me);
    const setMe = useOrgAuthStore((s) => s.setMe);
    const { consoleAvailable } = useClientPlatformAccess();
    const queryClient = useQueryClient();
    const { bundle, initialLoading, error } = useOnboardingApplication();

    const [companyName, setCompanyName] = useState("");
    const [legalName, setLegalName] = useState("");
    const [jurisdiction, setJurisdiction] =
        useState<IncorporationJurisdiction | null>(null);
    const [entityType, setEntityType] = useState<LegalEntityTypeDraft>(() => ({
        ...EMPTY_LEGAL_ENTITY_TYPE_DRAFT
    }));
    const [registeredAddress, setRegisteredAddress] = useState("");
    const [companyNumber, setCompanyNumber] = useState("");
    const [formationDocument, setFormationDocument] =
        useState<LiteFormationDocument | null>(null);
    const [uploadingDocument, setUploadingDocument] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [primaryContactSameAsSignatory, setPrimaryContactSameAsSignatory] =
        useState(true);
    const separateContactInitialized = useRef(false);
    const [primaryContactName, setPrimaryContactName] = useState("");
    const [primaryContactEmail, setPrimaryContactEmail] = useState("");
    // Only committed entries live here: the not-yet-added one is transient
    // state inside `WalletRows`, so an untouched form starts with no rows.
    const [wallets, setWallets] = useState<LiteWalletDraft[]>([]);
    const [signatoryName, setSignatoryName] = useState("");
    const [signatoryEmail, setSignatoryEmail] = useState("");
    const [signatoryTitle, setSignatoryTitle] = useState("");
    const [useCases, setUseCases] = useState<Set<UseCaseId>>(new Set());
    const CAPITAL_RANGE_VALUES: ReadonlySet<string> = new Set(
        CAPITAL_RANGE_OPTIONS.map((opt) => opt.value)
    );
    const isCapitalRange = (value: unknown): value is CapitalRange =>
        typeof value === "string" && CAPITAL_RANGE_VALUES.has(value);
    const [capitalRange, setCapitalRange] = useState<CapitalRange | null>(null);
    const [termsAccepted, setTermsAccepted] = useState(false);

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    // Validate-on-submit: per-field messages stay hidden until the first
    // blocked "Complete onboarding" attempt, then each clears live as its
    // field becomes valid (fieldErrors is re-derived every render).
    const [showValidation, setShowValidation] = useState(false);
    const fieldRefs = useRef<
        Partial<Record<LiteRequiredField, HTMLDivElement | null>>
    >({});
    // After a successful submit we swap the form for the pending-review screen
    // (lite no longer auto-approves — the issuer must review). Drives the result
    // immediately so there's no flash of the form before the bundle refetches.
    const [completed, setCompleted] = useState(false);

    // Seed the form once, after the first load resolves. Names come from the
    // org row (source of truth); jurisdiction + contact + intent rehydrate from
    // the application row / step payloads so a reload after a prior lite save
    // shows the captured values.
    const seeded = useRef(false);
    useEffect(() => {
        if (seeded.current || initialLoading) return;
        const org = me?.org
            ? { name: me.org.name, legalName: me.org.legalName ?? null }
            : null;
        const prefill = prefillLiteForm(
            org,
            bundle?.application ?? null,
            bundle?.steps
        );
        // Names are pre-seeded by the issuer at provisioning, so prefill both
        // and let the client edit (AC1). Display Name -> org.name, Legal Name
        // -> org.legalName.
        setCompanyName(prefill.companyName);
        setLegalName(prefill.legalName);
        setJurisdiction(prefill.jurisdiction);
        setEntityType(prefill.entityType);
        setRegisteredAddress(prefill.registeredAddress);
        setCompanyNumber(prefill.companyNumber);
        setFormationDocument(prefill.formationDocument);
        setPrimaryContactSameAsSignatory(prefill.primaryContactSameAsSignatory);
        // A saved checked contact is resolved from the signer, not a separate draft.
        separateContactInitialized.current =
            !prefill.primaryContactSameAsSignatory;
        setPrimaryContactName(prefill.primaryContactName);
        setPrimaryContactEmail(prefill.primaryContactEmail);
        setWallets(prefill.wallets);
        setSignatoryName(prefill.signatoryName);
        setSignatoryEmail(prefill.signatoryEmail);
        setSignatoryTitle(prefill.signatoryTitle);
        setUseCases(prefill.useCases);
        setCapitalRange(prefill.capitalRange);
        setTermsAccepted(prefill.termsAccepted);
        seeded.current = true;
    }, [initialLoading, bundle, me]);

    // A formation document read back from a prior save carries only its
    // uuid (the Legal Entity payload stores nothing else), so its caption
    // comes from the documents list. One no longer on the list was
    // replaced or removed: the slot empties for a fresh upload rather than
    // resubmitting a uuid the API would refuse. If the list itself cannot
    // be read the document is still on file, so it is captioned as such.
    const applicationUuid = bundle?.application.uuid;
    useEffect(() => {
        if (!applicationUuid || formationDocument?.filename !== null) {
            return;
        }
        let cancelled = false;
        void listOnboardingDocuments({
            applicationUuid,
            purpose: "formation_document"
        })
            .then((documents) => {
                if (cancelled) return;
                const match = documents.find(
                    (doc) => doc.uuid === formationDocument.documentUuid
                );
                setFormationDocument(
                    match
                        ? { documentUuid: match.uuid, filename: match.filename }
                        : null
                );
            })
            .catch(() => {
                if (cancelled) return;
                setFormationDocument({
                    ...formationDocument,
                    filename: "On file"
                });
            });
        return (): void => {
            cancelled = true;
        };
    }, [applicationUuid, formationDocument]);

    const formState: LiteFormState = {
        companyName,
        legalName,
        entityType,
        jurisdiction,
        registeredAddress,
        companyNumber,
        formationDocument,
        primaryContactSameAsSignatory,
        primaryContactName,
        primaryContactEmail,
        wallets,
        signatoryName,
        signatoryEmail,
        signatoryTitle,
        useCases,
        capitalRange,
        termsAccepted
    };
    // The Authorized Mint & Redeem Addresses print on the Minting Master
    // Agreement alone, so the register is shown, required and sent only for
    // a client the issuer has enabled Mint and Redeem for (the server
    // composes the minting step into that client's application).
    const applicationPurpose: OnboardingPurpose | undefined =
        bundle?.application.purpose;
    const mintingOnly = isMintingOnlyApplication(applicationPurpose);
    const signsMintingAgreement =
        mintingOnly || liteSignsMintingAgreement(bundle?.steps);
    const fieldErrors = liteFieldErrors(
        formState,
        signsMintingAgreement,
        applicationPurpose
    );
    const submittable = Object.keys(fieldErrors).length === 0;
    const fieldError = (field: LiteRequiredField): string | undefined =>
        showValidation ? fieldErrors[field] : undefined;

    /** Blocked submit attempt: reveal the per-field messages and bring the
     *  first invalid field into view (focusing its input when it has one). */
    const revealValidation = (): void => {
        setShowValidation(true);
        const first = LITE_REQUIRED_FIELD_ORDER.find((f) => fieldErrors[f]);
        if (!first) return;
        const el = fieldRefs.current[first];
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
        el?.querySelector("input")?.focus({ preventScroll: true });
    };

    /** Upload the picked file as this application's formation document.
     *  The slot holds one file: picking another replaces it, and the submit
     *  files whichever uuid the slot holds at the time. */
    const handleUpload = async (files: FileList): Promise<void> => {
        const file = files[0];
        if (!file || !applicationUuid) return;
        setUploadError(null);
        // Sim has no document store: the pick lands in the slot as-is, so
        // the form can be walked without a backend.
        if (isSimMode()) {
            setFormationDocument({
                documentUuid: `sim-document-${Date.now()}`,
                filename: file.name
            });
            return;
        }
        setUploadingDocument(true);
        try {
            const doc = await uploadOnboardingDocument({
                applicationUuid,
                purpose: "formation_document",
                file
            });
            setFormationDocument({
                documentUuid: doc.uuid,
                filename: doc.filename
            });
        } catch (err) {
            setUploadError(
                resolveClientFormError(
                    err,
                    "onboarding",
                    "The document was not uploaded. Choose the file again and retry."
                )
            );
        } finally {
            setUploadingDocument(false);
        }
    };

    const handleSave = async (): Promise<void> => {
        const payload = buildLitePayload(
            formState,
            signsMintingAgreement,
            applicationPurpose
        );
        if (!bundle || !payload || submitting) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            await submitLite(bundle.application.uuid, payload);

            // Keep the dashboard greeting + org identity in sync with the edit.
            if (me) {
                setMe({
                    ...me,
                    org: {
                        ...me.org,
                        name: payload.companyName,
                        legalName: payload.legalName
                    }
                });
            }

            // The gate reads applications via React Query (30s staleTime);
            // invalidate so it picks up the new in_review status (and the
            // eventual approval, after which it lets the client through).
            await queryClient.invalidateQueries({
                queryKey: ["client-onboarding-applications"]
            });

            // Swap the form for the pending-review screen — the application is
            // now awaiting issuer approval (no dashboard redirect).
            setCompleted(true);
        } catch (err) {
            setSubmitError(
                resolveClientFormError(
                    err,
                    "onboarding",
                    "Onboarding was not submitted. Check the required fields and try again."
                )
            );
            setSubmitting(false);
        }
    };

    if (initialLoading) {
        return (
            <main className="min-h-dvh bg-surface-ground">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-10 pb-50" />
            </main>
        );
    }

    if (error) {
        return (
            <LiteShell>
                <Alert variant="destructive" outline>
                    <AlertTitle>Couldn&apos;t load your onboarding</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            </LiteShell>
        );
    }

    if (!bundle) {
        return (
            <LiteShell>
                <Alert variant="info" outline>
                    <AlertTitle>No onboarding application yet</AlertTitle>
                    <AlertDescription>
                        Reach out to your BD contact to get your onboarding
                        invitation. Once it&apos;s issued you can complete it
                        from here.
                    </AlertDescription>
                </Alert>
            </LiteShell>
        );
    }

    // The server's status decides the screen; `completed` only bridges the
    // gap between this visit's submit and the refetch that carries the new
    // status (see `liteScreen`).
    const screen = liteScreen(bundle.application.status, completed);

    if (screen === "approved") {
        return (
            <LiteApprovedCelebration
                applicationUuid={bundle.application.uuid}
                firstVisit={bundle.application.approvalAcknowledgedAt === null}
                agreementFullySigned={Boolean(
                    bundle.agreements?.some(
                        (agreement) =>
                            agreement.agreementType === "minting_master" &&
                            agreement.status === "signed"
                    )
                )}
                mintingOnly={mintingOnly}
                consoleAvailable={consoleAvailable}
            />
        );
    }

    if (screen === "pending_review") {
        return (
            <LiteResultShell>
                <PendingReviewCard />
            </LiteResultShell>
        );
    }

    // The issuer approved and the agreements are out for signature. The
    // console opens on the last signature, so the page stays here and
    // tracks the envelopes. Only reachable while the server sends
    // agreements; with them handled outside the platform an approval goes
    // straight to `approved` and the celebration above.
    if (screen === "agreements_out") {
        return (
            <LiteResultShell>
                <AgreementsOutCard
                    signatoryEmail={bundle.application.signatoryEmail}
                    agreements={bundle.agreements ?? []}
                    mintingOnly={mintingOnly}
                />
            </LiteResultShell>
        );
    }

    const rejectionReason = bundle.application.rejectionReason?.trim() || null;

    return (
        <main className="min-h-dvh bg-surface-ground">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-10 pb-50">
                <LiteHeader />

                {rejectionReason ? (
                    <Alert variant="destructive" outline className="mt-6">
                        <AlertTitle>Submission returned for changes</AlertTitle>
                        <AlertDescription>{rejectionReason}</AlertDescription>
                    </Alert>
                ) : null}

                <SectionHeading
                    variant="h2"
                    className={rejectionReason ? "pt-8" : "pt-6"}
                >
                    Legal Entity
                </SectionHeading>

                <motion.section
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, ease: easings.outCubic }}
                >
                    <div
                        ref={(el) => {
                            fieldRefs.current.legalName = el;
                        }}
                    >
                        <FormCard
                            title="Legal Name"
                            htmlFor={fieldControlId("legalName")}
                            hint="The entity's full legal name as it appears on your formation document."
                        >
                            <Input
                                id={fieldControlId("legalName")}
                                aria-describedby={
                                    fieldError("legalName")
                                        ? fieldErrorId("legalName")
                                        : undefined
                                }
                                value={legalName}
                                onChange={(e) => setLegalName(e.target.value)}
                                placeholder="eg. Crescent Wealth Holdings, LLC"
                                state={
                                    fieldError("legalName")
                                        ? "error"
                                        : undefined
                                }
                            />
                            <FieldError
                                field="legalName"
                                message={fieldError("legalName")}
                            />
                        </FormCard>
                    </div>

                    <FormCard
                        title="Display Name (Optional)"
                        htmlFor={fieldControlId("companyName")}
                        hint="The name shown across the app."
                    >
                        <Input
                            id={fieldControlId("companyName")}
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            placeholder="eg. Crescent Wealth"
                        />
                    </FormCard>

                    {!mintingOnly ? (
                        <div
                            ref={(el) => {
                                fieldRefs.current.entityType = el;
                            }}
                        >
                            <FormCard
                                title="Entity Type"
                                hint="The formal type shown in your formation records."
                            >
                                <LegalEntityTypeSelect
                                    id={fieldControlId("entityType")}
                                    value={entityType}
                                    onChange={setEntityType}
                                    error={Boolean(fieldError("entityType"))}
                                    errorId={
                                        fieldError("entityType")
                                            ? fieldErrorId("entityType")
                                            : undefined
                                    }
                                />
                                <FieldError
                                    field="entityType"
                                    message={fieldError("entityType")}
                                />
                            </FormCard>
                        </div>
                    ) : null}

                    <div
                        ref={(el) => {
                            fieldRefs.current.jurisdiction = el;
                        }}
                    >
                        <FormCard
                            title="Jurisdiction of Incorporation"
                            htmlFor={fieldControlId("jurisdiction")}
                            hint="Where the entity was formed."
                        >
                            <CountrySelect
                                id={fieldControlId("jurisdiction")}
                                value={jurisdiction}
                                onChange={setJurisdiction}
                                error={Boolean(fieldError("jurisdiction"))}
                            />
                            <FieldError
                                field="jurisdiction"
                                message={fieldError("jurisdiction")}
                            />
                        </FormCard>
                    </div>

                    <div
                        ref={(el) => {
                            fieldRefs.current.registeredAddress = el;
                        }}
                    >
                        <FormCard
                            title="Registered Address"
                            htmlFor={fieldControlId("registeredAddress")}
                            hint="The entity's registered office, as filed with its registry."
                        >
                            <Input
                                id={fieldControlId("registeredAddress")}
                                aria-describedby={
                                    fieldError("registeredAddress")
                                        ? fieldErrorId("registeredAddress")
                                        : undefined
                                }
                                value={registeredAddress}
                                onChange={(e) =>
                                    setRegisteredAddress(e.target.value)
                                }
                                placeholder="e.g. 4th Floor, 12 Bridge Street, Road Town, Tortola VG1110"
                                state={
                                    fieldError("registeredAddress")
                                        ? "error"
                                        : undefined
                                }
                            />
                            <FieldError
                                field="registeredAddress"
                                message={fieldError("registeredAddress")}
                            />
                        </FormCard>
                    </div>

                    <div
                        ref={(el) => {
                            fieldRefs.current.companyNumber = el;
                        }}
                    >
                        <FormCard
                            title="Entity Number"
                            htmlFor={fieldControlId("companyNumber")}
                            hint="The registration number issued by the jurisdiction above."
                        >
                            <Input
                                id={fieldControlId("companyNumber")}
                                aria-describedby={
                                    fieldError("companyNumber")
                                        ? fieldErrorId("companyNumber")
                                        : undefined
                                }
                                value={companyNumber}
                                onChange={(e) =>
                                    setCompanyNumber(e.target.value)
                                }
                                placeholder="e.g. 7421889"
                                state={
                                    fieldError("companyNumber")
                                        ? "error"
                                        : undefined
                                }
                            />
                            <FieldError
                                field="companyNumber"
                                message={fieldError("companyNumber")}
                            />
                        </FormCard>
                    </div>

                    <div
                        ref={(el) => {
                            fieldRefs.current.formationDocument = el;
                        }}
                    >
                        <FormCard
                            title="Formation Document"
                            hint="The certificate of incorporation, articles of organization, or the equivalent your registry issued."
                        >
                            <UploadFiles
                                size="sm"
                                accept={DOCUMENT_ACCEPT}
                                maxSize={DOCUMENT_MAX_SIZE}
                                state={
                                    uploadingDocument
                                        ? "uploading"
                                        : uploadError
                                          ? "error"
                                          : formationDocument === null
                                            ? "empty"
                                            : formationDocument.filename ===
                                                null
                                              ? "loading"
                                              : "success"
                                }
                                filename={
                                    formationDocument?.filename ?? undefined
                                }
                                error={uploadError ?? undefined}
                                onSelect={(files) => void handleUpload(files)}
                            />
                            <FieldError
                                field="formationDocument"
                                message={fieldError("formationDocument")}
                            />
                        </FormCard>
                    </div>

                    <SectionHeading variant="h2" className="pt-10">
                        Contacts
                    </SectionHeading>

                    <div
                        ref={(el) => {
                            fieldRefs.current.signatoryName = el;
                        }}
                    >
                        <FormCard
                            title="Signatory's Legal Name"
                            htmlFor={fieldControlId("signatoryName")}
                            hint="Who signs the agreements on the entity's behalf."
                        >
                            <Input
                                id={fieldControlId("signatoryName")}
                                aria-describedby={
                                    fieldError("signatoryName")
                                        ? fieldErrorId("signatoryName")
                                        : undefined
                                }
                                value={signatoryName}
                                onChange={(e) =>
                                    setSignatoryName(e.target.value)
                                }
                                placeholder="e.g. Sarah Chen"
                                state={
                                    fieldError("signatoryName")
                                        ? "error"
                                        : undefined
                                }
                            />
                            <FieldError
                                field="signatoryName"
                                message={fieldError("signatoryName")}
                            />
                        </FormCard>
                    </div>

                    <div
                        ref={(el) => {
                            fieldRefs.current.signatoryEmail = el;
                        }}
                    >
                        <FormCard
                            title="Signatory's Email"
                            htmlFor={fieldControlId("signatoryEmail")}
                            hint="Where the agreements are sent for signature."
                        >
                            <Input
                                id={fieldControlId("signatoryEmail")}
                                aria-describedby={
                                    fieldError("signatoryEmail")
                                        ? fieldErrorId("signatoryEmail")
                                        : undefined
                                }
                                type="email"
                                value={signatoryEmail}
                                onChange={(e) =>
                                    setSignatoryEmail(e.target.value)
                                }
                                placeholder="e.g. sarah@crescent.com"
                                state={
                                    fieldError("signatoryEmail")
                                        ? "error"
                                        : undefined
                                }
                            />
                            <FieldError
                                field="signatoryEmail"
                                message={fieldError("signatoryEmail")}
                            />
                        </FormCard>
                    </div>

                    <div
                        ref={(el) => {
                            fieldRefs.current.signatoryTitle = el;
                        }}
                    >
                        <FormCard
                            title="Signatory's Title"
                            htmlFor={fieldControlId("signatoryTitle")}
                            hint="Their role in the entity."
                        >
                            <Input
                                id={fieldControlId("signatoryTitle")}
                                aria-describedby={
                                    fieldError("signatoryTitle")
                                        ? fieldErrorId("signatoryTitle")
                                        : undefined
                                }
                                value={signatoryTitle}
                                onChange={(e) =>
                                    setSignatoryTitle(e.target.value)
                                }
                                placeholder="e.g. Managing Director"
                                state={
                                    fieldError("signatoryTitle")
                                        ? "error"
                                        : undefined
                                }
                            />
                            <FieldError
                                field="signatoryTitle"
                                message={fieldError("signatoryTitle")}
                            />
                        </FormCard>
                    </div>

                    <FormCard
                        title="Primary Contact"
                        hint="The entity's primary contact with Solomon Labs."
                    >
                        <Select
                            value={
                                primaryContactSameAsSignatory
                                    ? "signatory"
                                    : "separate"
                            }
                            onValueChange={(value) => {
                                if (
                                    value !== "signatory" &&
                                    value !== "separate"
                                )
                                    return;
                                if (
                                    value === "separate" &&
                                    !separateContactInitialized.current
                                ) {
                                    setPrimaryContactName(signatoryName);
                                    setPrimaryContactEmail(signatoryEmail);
                                    separateContactInitialized.current = true;
                                }
                                setPrimaryContactSameAsSignatory(
                                    value === "signatory"
                                );
                            }}
                        >
                            <SelectTrigger
                                aria-label="Primary contact source"
                                interaction="lightbar"
                                className="w-full min-w-0"
                            >
                                <SelectValue>
                                    {primaryContactSameAsSignatory
                                        ? "Same as Signatory"
                                        : "Different Contact"}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="signatory">
                                    Same as Signatory
                                </SelectItem>
                                <SelectItem value="separate">
                                    Different Contact
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </FormCard>
                    {!primaryContactSameAsSignatory && (
                        <>
                            <div
                                ref={(el) => {
                                    fieldRefs.current.primaryContactName = el;
                                }}
                            >
                                <FormCard
                                    title="Primary Contact Name"
                                    htmlFor={fieldControlId(
                                        "primaryContactName"
                                    )}
                                >
                                    <Input
                                        id={fieldControlId(
                                            "primaryContactName"
                                        )}
                                        aria-describedby={
                                            fieldError("primaryContactName")
                                                ? fieldErrorId(
                                                      "primaryContactName"
                                                  )
                                                : undefined
                                        }
                                        value={primaryContactName}
                                        onChange={(e) =>
                                            setPrimaryContactName(
                                                e.target.value
                                            )
                                        }
                                        placeholder="e.g. Sarah Chen"
                                        state={
                                            fieldError("primaryContactName")
                                                ? "error"
                                                : undefined
                                        }
                                    />
                                    <FieldError
                                        field="primaryContactName"
                                        message={fieldError(
                                            "primaryContactName"
                                        )}
                                    />
                                </FormCard>
                            </div>

                            <div
                                ref={(el) => {
                                    fieldRefs.current.primaryContactEmail = el;
                                }}
                            >
                                <FormCard
                                    title="Primary Contact Email"
                                    htmlFor={fieldControlId(
                                        "primaryContactEmail"
                                    )}
                                    hint="The email of the primary contact for any follow-ups and notifications."
                                >
                                    <Input
                                        id={fieldControlId(
                                            "primaryContactEmail"
                                        )}
                                        aria-describedby={
                                            fieldError("primaryContactEmail")
                                                ? fieldErrorId(
                                                      "primaryContactEmail"
                                                  )
                                                : undefined
                                        }
                                        type="email"
                                        value={primaryContactEmail}
                                        onChange={(e) =>
                                            setPrimaryContactEmail(
                                                e.target.value
                                            )
                                        }
                                        placeholder="e.g. ops@crescent.com"
                                        state={
                                            fieldError("primaryContactEmail")
                                                ? "error"
                                                : undefined
                                        }
                                    />
                                    <FieldError
                                        field="primaryContactEmail"
                                        message={fieldError(
                                            "primaryContactEmail"
                                        )}
                                    />
                                </FormCard>
                            </div>
                        </>
                    )}

                    {signsMintingAgreement && (
                        <>
                            <SectionHeading variant="h2" className="pt-10">
                                Authorized Mint & Redeem Addresses
                            </SectionHeading>

                            <div
                                ref={(el) => {
                                    fieldRefs.current.wallets = el;
                                }}
                            >
                                <WalletRows
                                    wallets={wallets}
                                    onChange={setWallets}
                                    invalid={Boolean(fieldError("wallets"))}
                                />
                                <FieldError
                                    field="wallets"
                                    message={fieldError("wallets")}
                                />
                            </div>
                        </>
                    )}

                    <SectionHeading variant="h2" className="pt-10">
                        Intended Use{mintingOnly ? " (optional)" : ""}
                    </SectionHeading>

                    <div
                        ref={(el) => {
                            fieldRefs.current.useCases = el;
                        }}
                    >
                        <FormCard
                            title="Use Cases"
                            hint={
                                mintingOnly
                                    ? "Optional. Pick every use case that applies."
                                    : "Pick every use case that applies."
                            }
                        >
                            <ToggleGroup
                                multiple
                                value={[...useCases]}
                                onValueChange={(values) =>
                                    setUseCases(
                                        new Set(values.filter(isUseCaseId))
                                    )
                                }
                                spacing={1.5}
                                size="sm"
                                // The group is `w-fit flex-row` with no wrap,
                                // so a row of chips wider than the viewport
                                // overflowed sideways on mobile rather than
                                // wrapping onto a second line.
                                className="w-full flex-wrap"
                                aria-label="Use cases"
                            >
                                {USE_CASE_OPTIONS.map((opt) => (
                                    <ToggleGroupItem
                                        key={opt.value}
                                        value={opt.value}
                                    >
                                        {opt.label}
                                    </ToggleGroupItem>
                                ))}
                            </ToggleGroup>
                            <FieldError
                                field="useCases"
                                message={fieldError("useCases")}
                            />
                        </FormCard>
                    </div>

                    <FormCard
                        title="Expected Capital Range"
                        hint="Estimated deployment over the next 3 months."
                    >
                        <ToggleGroup
                            requireSelection
                            value={capitalRange ? [capitalRange] : []}
                            onValueChange={(values) => {
                                const next = values[0];
                                if (isCapitalRange(next)) setCapitalRange(next);
                            }}
                            spacing={1.5}
                            size="sm"
                            // Same overflow as Use Cases above.
                            className="w-full flex-wrap"
                            aria-label="Expected capital range"
                        >
                            {CAPITAL_RANGE_OPTIONS.map((opt) => (
                                <ToggleGroupItem
                                    key={opt.value}
                                    value={opt.value}
                                >
                                    {opt.label}
                                </ToggleGroupItem>
                            ))}
                        </ToggleGroup>
                    </FormCard>

                    <SectionHeading variant="h2" className="pt-10">
                        Terms of Service
                    </SectionHeading>

                    <div
                        ref={(el) => {
                            fieldRefs.current.termsAccepted = el;
                        }}
                    >
                        <FormCard
                            title="Accept Terms of Service"
                            hint="Your submission is made under these terms."
                        >
                            {/* The whole sentence is the label, so clicking
                                any word of it toggles the box; the link
                                inside opens the terms without toggling. */}
                            <Text
                                variant="body"
                                render={<label />}
                                className="flex items-start gap-2 text-foreground-base cursor-pointer"
                            >
                                {/* `items-center` centred the box against the
                                    whole sentence, so it floated between the
                                    two lines once the label wrapped. The box
                                    is centred on ONE line instead: `1lh` is
                                    the label's own line-height, so this holds
                                    at any breakpoint without pinning a px
                                    offset to a font size. */}
                                <span className="flex h-[1lh] shrink-0 items-center">
                                    <Checkbox
                                        checked={termsAccepted}
                                        onCheckedChange={(checked) =>
                                            setTermsAccepted(checked === true)
                                        }
                                        aria-describedby={
                                            fieldError("termsAccepted")
                                                ? fieldErrorId("termsAccepted")
                                                : undefined
                                        }
                                        aria-invalid={
                                            fieldError("termsAccepted")
                                                ? true
                                                : undefined
                                        }
                                    />
                                </span>
                                <span>
                                    I have read and agree to the{" "}
                                    <a
                                        href={TERMS_OF_SERVICE_URL}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="underline underline-offset-2 hover:text-foreground-muted"
                                    >
                                        Terms of Service
                                    </a>{" "}
                                    on behalf of the entity above.
                                </span>
                            </Text>
                            <FieldError
                                field="termsAccepted"
                                message={fieldError("termsAccepted")}
                            />
                        </FormCard>
                    </div>

                    <section className="mt-6 pt-5">
                        {/* Stays clickable when the form is incomplete: a
                            blocked attempt reveals the per-field messages and
                            scrolls to the first invalid field, instead of the
                            silent no-op a disabled button would give. */}
                        <Button
                            variant="raised"
                            interaction="light-bar"
                            size="lg"
                            fullWidth
                            disabled={submitting}
                            onClick={() => {
                                if (!submittable) {
                                    revealValidation();
                                    return;
                                }
                                void handleSave();
                            }}
                        >
                            {submitting ? "Saving…" : "Submit"}
                        </Button>
                        {submitError && (
                            // The terminal "not submitted" error, and it was
                            // the quietest thing on the page: a plain <p> with
                            // no live region, rendered under the less urgent
                            // rejection Alert above it. DSFieldError carries
                            // role="alert", the same reason the per-field
                            // messages moved to it.
                            <DSFieldError className="pt-3">
                                {submitError}
                            </DSFieldError>
                        )}
                    </section>
                </motion.section>
            </div>
        </main>
    );
}

/** DOM id of a field's inline error message. Shared by `FieldError` and the
 *  `aria-describedby` on the control it describes, so a screen reader reads
 *  the reason for the invalid state, not just that it is invalid. */
function fieldErrorId(field: LiteRequiredField): string {
    return `lite-onboarding-${field}-error`;
}

/**
 * Authorized Address Register (Order Form §2.3). The register's six other
 * columns are fixed per pathway in Annex X.5, so the applicant is asked only
 * for the label, the address, and the assets it mints against.
 *
 * Shape: one raised `Card` holds the entry form (the only elevated thing in
 * the section, per ui-guide 1a/5a — it carries the section's primary action),
 * and everything already added collapses into a hairline `List` above it. A
 * row swaps in place for the same three fields when clicked, so editing never
 * lands somewhere else on the page.
 *
 * Each address must pass the shared wallet verification ceremony before
 * the application can be submitted.
 */
/**
 * Column template shared by the Register's heading row and its data rows, so
 * the two cannot drift apart. The trailing spacer in the heading mirrors the
 * two `size-4` icon buttons plus their `gap-1`.
 */
// why 11rem: the asset column must be a FIXED track, not `auto`. The heading
// and the rows are separate grids, and an `auto` track sizes to each one's own
// content — "Primary Asset(s)" vs a badge — which pushes the columns out of
// alignment. 11rem fits the heading and up to three badges.
const ADDRESS_ROW_GRID =
    "grid w-full min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_11rem] items-center gap-4";

function WalletRows({
    wallets,
    onChange,
    invalid
}: {
    wallets: LiteWalletDraft[];
    onChange: Dispatch<SetStateAction<LiteWalletDraft[]>>;
    invalid: boolean;
}): ReactElement {
    // Transient UI state only. The register itself stays lifted in
    // `OnboardingLite`: this is the not-yet-added entry.
    const [draft, setDraft] = useState<LiteWalletDraft>({
        ...EMPTY_LITE_WALLET
    });

    // An address may sit on the Register once. A second entry for the same
    // address would put the same key under two roles with no way to say which
    // one governs, so the add is refused rather than silently merged.
    const isDuplicate = wallets.some(
        (w) => w.address.trim() === draft.address.trim()
    );
    // Gate the add so a row only ever lands in form state complete. That is
    // what lets the list read as a register of settled entries rather than a
    // pile of half-filled ones.
    const canAdd =
        draft.address.trim().length > 0 &&
        draft.primaryAssets.length > 0 &&
        !isDuplicate;

    const addDraft = (): void => {
        if (!canAdd) return;
        onChange([
            ...wallets,
            {
                ...draft,
                id: nextLiteWalletId(),
                address: draft.address.trim(),
                label: draft.label.trim()
            }
        ]);
        setDraft({ ...EMPTY_LITE_WALLET });
    };

    const remove = (id: string): void => {
        onChange(wallets.filter((w) => w.id !== id));
    };

    return (
        <div className="flex flex-col gap-6 pt-6">
            <AddressForm
                heading="Add Authorized Address"
                idPrefix="lite-wallet-add"
                value={draft}
                onChange={setDraft}
                invalid={invalid}
                duplicate={isDuplicate}
                actions={
                    <>
                        <Button
                            variant="raised"
                            interaction="lifted"
                            size="sm"
                            disabled={!canAdd}
                            onClick={addDraft}
                        >
                            +Add Wallet
                        </Button>
                        {isDuplicate && (
                            <Text
                                variant="fine-print"
                                render={<span />}
                                className="text-error-base"
                            >
                                This address is already on the register.
                            </Text>
                        )}
                    </>
                }
            />

            {wallets.length > 0 && (
                <div>
                    {/* Heading row. Mirrors ListItem's own `gap-3 px-2` and
                        reserves the trailing width of the remove button, so
                        the labels sit over their columns. */}
                    <div className="flex gap-3 px-2 pb-2">
                        <div className={ADDRESS_ROW_GRID}>
                            <Text
                                variant="label"
                                render={<span />}
                                className="text-foreground-muted"
                            >
                                Label
                            </Text>
                            <Text
                                variant="label"
                                render={<span />}
                                className="text-foreground-muted"
                            >
                                Address
                            </Text>
                            <Text
                                variant="label"
                                render={<span />}
                                className="justify-self-end text-foreground-muted"
                            >
                                Primary Asset(s)
                            </Text>
                        </div>
                        <span
                            aria-hidden="true"
                            className="flex shrink-0 items-center gap-1"
                        >
                            <span className="size-5" />
                        </span>
                    </div>
                    <List>
                        {wallets.map((wallet) => (
                            <ListItem
                                key={wallet.id}
                                itemKey={wallet.id}
                                padding="px-2 py-3"
                                trailing={
                                    // A row is not itself interactive, so
                                    // this is the only control on it.
                                    <div className="flex items-center gap-1">
                                        <ButtonIcon
                                            variant="transparent"
                                            size="lg"
                                            aria-label={`Remove ${
                                                wallet.label.trim() || "address"
                                            }`}
                                            onClick={() => remove(wallet.id)}
                                            icon={
                                                <TrashIcon aria-hidden="true" />
                                            }
                                        />
                                    </div>
                                }
                            >
                                <div className={ADDRESS_ROW_GRID}>
                                    <Text
                                        variant="small"
                                        render={<span />}
                                        className="min-w-0 truncate text-foreground-base"
                                    >
                                        {wallet.label.trim() ||
                                            truncateAddress(
                                                wallet.address,
                                                6,
                                                6
                                            )}
                                    </Text>
                                    <div className="min-w-0">
                                        <Text
                                            variant="number"
                                            className="block truncate text-foreground-neutral"
                                        >
                                            {truncateAddress(
                                                wallet.address,
                                                6,
                                                6
                                            )}
                                        </Text>
                                    </div>
                                    <div className="flex flex-wrap justify-end gap-1">
                                        {/* Canonical column order, not the
                                            order the applicant happened to
                                            click, so every row reads the
                                            same way. */}
                                        {PRIMARY_ASSET_OPTIONS.filter((asset) =>
                                            wallet.primaryAssets.includes(asset)
                                        ).map((asset) => (
                                            <Badge
                                                key={asset}
                                                variant="fill"
                                                color={ASSET_BADGE_COLOR[asset]}
                                                size="sm"
                                            >
                                                {asset}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            </ListItem>
                        ))}
                    </List>
                </div>
            )}
        </div>
    );
}

/**
 * The three Register columns the applicant fills in, on the raised surface
 * that marks where the section is acted on. Rows are add-or-remove only:
 * correcting an entry means removing it and adding it again, which keeps the
 * list a record of settled entries.
 */
function AddressForm({
    heading,
    hint,
    idPrefix,
    value,
    onChange,
    invalid,
    duplicate = false,
    disabled = false,
    actions
}: {
    heading: string;
    hint?: string;
    /** Prefix for the control ids the labels point at. Unique per form. */
    idPrefix: string;
    value: LiteWalletDraft;
    onChange: (next: LiteWalletDraft) => void;
    invalid: boolean;
    /** The address already sits on the Register. Marks the Address field
     *  rather than only disabling the action, so the offending field is
     *  obvious. */
    duplicate?: boolean;
    disabled?: boolean;
    actions: ReactNode;
}): ReactElement {
    return (
        <Card surface="raised" border={false}>
            <CardHeader>
                <CardTitle>{heading}</CardTitle>
                {hint ? <CardDescription>{hint}</CardDescription> : null}
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
                <Field>
                    <FieldLabel htmlFor={`${idPrefix}-label`}>
                        <Text variant="small" render={<span />}>
                            Label (Optional)
                        </Text>
                    </FieldLabel>
                    <Input
                        id={`${idPrefix}-label`}
                        disabled={disabled}
                        value={value.label}
                        onChange={(e) =>
                            onChange({ ...value, label: e.target.value })
                        }
                        placeholder="e.g. Treasury"
                    />
                </Field>

                <Field>
                    <FieldLabel htmlFor={`${idPrefix}-address`}>
                        <Text variant="small" render={<span />}>
                            Address
                        </Text>
                    </FieldLabel>
                    <Input
                        id={`${idPrefix}-address`}
                        disabled={disabled}
                        value={value.address}
                        onChange={(e) =>
                            onChange({ ...value, address: e.target.value })
                        }
                        placeholder="Solana address"
                        state={
                            duplicate ||
                            (invalid && value.address.trim().length === 0)
                                ? "error"
                                : undefined
                        }
                    />
                </Field>

                <Field>
                    {/* A composite control, so the title is a plain div: a
                        `<label>` pointing at a group would be a lie. */}
                    <FieldTitle>
                        <Text variant="small" render={<span />}>
                            Primary Asset(s)
                        </Text>
                    </FieldTitle>
                    <ToggleGroup
                        multiple
                        disabled={disabled}
                        value={value.primaryAssets}
                        onValueChange={(values) =>
                            onChange({
                                ...value,
                                primaryAssets: values.filter(
                                    (v): v is string => typeof v === "string"
                                )
                            })
                        }
                        spacing={1.5}
                        size="sm"
                        aria-label="Primary assets used to mint"
                    >
                        {PRIMARY_ASSET_OPTIONS.map((asset) => (
                            <ToggleGroupItem key={asset} value={asset}>
                                {asset}
                            </ToggleGroupItem>
                        ))}
                    </ToggleGroup>
                </Field>
            </CardContent>
            <CardFooter>{actions}</CardFooter>
        </Card>
    );
}

/** Id of a field's control, so `FormCard`'s `<label htmlFor>` has a target.
 *  Only for fields that wrap a single focusable control. Wider than
 *  `LiteRequiredField` because the contact NAME is labelled but optional. */
function fieldControlId(
    field: LiteRequiredField | "primaryContactName" | "companyName"
): string {
    return `lite-onboarding-${field}`;
}

/** Inline required-field message, shown under the field's control after a
 *  blocked submit attempt. Same tone as the submit error. */
function FieldError({
    field,
    message
}: {
    field: LiteRequiredField;
    message?: string;
}): ReactElement | null {
    if (!message) return null;
    // DSFieldError carries role="alert", so the message is announced when it
    // appears. The local version this replaced did not.
    return (
        <DSFieldError id={fieldErrorId(field)} className="pt-2">
            {message}
        </DSFieldError>
    );
}

/** Mirrors the full hub's `ApplicationHeader` (OnboardingShell) so the page
 *  chrome is identical on both sides of the lite-onboarding flag. */
function LiteHeader(): ReactElement {
    const me = useOrgAuthStore((s) => s.me);
    const { logout } = useOrgAuth(CLIENT_OAUTH_CONFIG);
    const userName = me?.user.fullName ?? me?.user.email ?? null;

    return (
        <header className="pt-2 pb-6 border-b border-line-base flex flex-col gap-2">
            {/* Row 1 — title and brand mark. The mark stacks above the title
                on mobile (`flex-col-reverse`, since Heading emits `actions`
                after the title) and sits beside it from `md` up. */}
            <Heading
                title={"USDv for Businesses Onboarding"}
                className="flex flex-col-reverse items-start gap-3 md:flex-row md:items-center md:gap-4"
                actions={
                    <SolomonLogo
                        className="h-6 w-auto text-foreground-base md:h-10"
                        title="Solomon"
                    />
                }
            />
            {/* Row 2 — the "who am I / switch" affordance, full width under
                the title row rather than inside Heading's title stack. */}
            {userName && (
                <Text
                    variant="small"
                    render={<span />}
                    className="text-foreground-muted inline-flex items-center gap-1 self-start"
                >
                    Not {userName}?
                    <DSButton
                        type="button"
                        variant="link"
                        interaction="underline"
                        /* `xs` is the size whose text variant is `small` —
                           matching the sentence it sits in. `2xs` maps to
                           `fine-print` and read a step smaller than the text
                           around it. The height/padding the size also carries
                           are stripped below, so size is picked purely for
                           its type. */
                        size="xs"
                        /* `leading` (not a child) so the icon sits outside the
                           button-label span and the `gap` actually applies;
                           `xs` would otherwise default to gap-1.5. */
                        leading={<ArrowRightLeftLongIcon className="size-4" />}
                        gap="1"
                        onClick={() => void logout()}
                        className="px-0 h-auto"
                    >
                        Switch user
                    </DSButton>
                </Text>
            )}
        </header>
    );
}

/** Page chrome shared by the lite view's non-form states (loading-resolved
 *  error / no-application / already-approved). */
function LiteShell({ children }: { children: ReactElement }): ReactElement {
    return (
        <main className="min-h-dvh bg-surface-ground">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-10 pb-50">
                {children}
            </div>
        </main>
    );
}

/** Page chrome for the post-submit result states (success celebration /
 *  pending-review): the same `LiteHeader` the form uses on top — so the page
 *  still reads as "USDv for Businesses Onboarding" — with the centered hero card
 *  below it spanning full width (the card handles its own `mt-25` + centering).
 *  Rendered fullPage (no sidebar), matching the live `/onboarding` route. */
function LiteResultShell({
    children
}: {
    children: ReactElement;
}): ReactElement {
    return (
        <main className="min-h-dvh bg-surface-ground">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-10">
                <LiteHeader />
            </div>
            {children}
        </main>
    );
}

/**
 * Post-approval welcome shown the first time a client reaches `/onboarding`
 * after the issuer approves them (the `OnboardingGate` routes that one sign-in
 * here; see `resolveOnboardingGateDecision`).
 *
 * On mount it records the acknowledgement, then lets the celebration route on.
 * The sequencing matters:
 *   1. Fire `acknowledgeApproval` exactly once (StrictMode-safe via a ref).
 *   2. On success, patch the `client-onboarding-applications` query — the one
 *      the gate reads — so the client reads as acknowledged, THEN enter the
 *      `acked` phase. The cache is patched before any navigation is allowed.
 *   3. On failure, stay put with a retry affordance — never navigate, which
 *      would loop straight back through the gate.
 *
 * ALL navigation (the celebration's auto-redirect timer AND its manual CTA)
 * funnels through `onContinue`, which only records intent; the actual
 * `navigate("/positions")` fires from an effect gated on `phase === "acked"`.
 * So neither the timer nor an early CTA click can route before the gate would
 * allow it — even if the ack POST is slow. We only ever write the cache from
 * the server's successful response (no optimistic stamp), so a failed POST
 * can't leak a false "acknowledged".
 */
function LiteApprovedCelebration({
    applicationUuid,
    firstVisit,
    agreementFullySigned,
    mintingOnly,
    consoleAvailable
}: {
    applicationUuid: string;
    firstVisit: boolean;
    agreementFullySigned: boolean;
    mintingOnly: boolean;
    consoleAvailable: boolean;
}): ReactElement {
    // Approved external clients remain here after acknowledgement. A remount
    // is not a new first visit; the server timestamp owns that decision.
    const {
        phase,
        requestContinue: handleContinue,
        retry: runAck
    } = useApprovalAcknowledgement({
        applicationUuid,
        firstVisit,
        allowConsoleNavigation: consoleAvailable
    });

    return (
        <LiteResultShell>
            <>
                {/* Celebration renders immediately; both its timer and CTA go
                    through onContinue, which only navigates once acked. The
                    timer itself stays disabled until the ack succeeds so the
                    4s countdown reads as "after you're set up". */}
                <CelebrationCard
                    autoRedirectMs={
                        phase === "acked" && consoleAvailable ? 4000 : null
                    }
                    onContinue={handleContinue}
                    title={
                        mintingOnly
                            ? "You've successfully onboarded with Solomon Labs"
                            : undefined
                    }
                    subtitle={
                        mintingOnly
                            ? agreementFullySigned
                                ? "Your Minting Master Agreement has been fully signed. Solomon will contact you separately to coordinate USDv minting and redemption."
                                : "Solomon will contact you for any remaining steps to mint and redeem."
                            : undefined
                    }
                    subtitleSpacing="relaxed"
                />
                {phase === "error" && (
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 text-center">
                        <DSFieldError className="pb-0">
                            We couldn&apos;t finish setting up your dashboard.
                            Please retry.
                        </DSFieldError>
                        <Button
                            variant="raised"
                            interaction="light-bar"
                            size="md"
                            className="mt-3"
                            onClick={() => void runAck()}
                        >
                            Retry
                        </Button>
                    </div>
                )}
            </>
        </LiteResultShell>
    );
}
